import { type TranslationKey } from '../types/typedTranslationKeys';
import { type UIFacingPreferences } from '../hooks/stores/search/slices/preferencesSlice';

export const GOOD_PREFERENCE_SCORE_THRESHOLD = 0.5;
export const VERY_GOOD_PREFERENCE_SCORE_THRESHOLD = 0.7;
export const EXCELLENT_PREFERENCE_SCORE_THRESHOLD = 0.9;

export const PROMPT_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const;
export const PROMPT_SUGGESTION_COUNT = 4;

export type PromptNumber = (typeof PROMPT_NUMBERS)[number];

export const INTERNAL_PREFERENCE_PREFIX = 'EV_';

export const PREFERENCES_TRANSLATION_MAP: Record<keyof UIFacingPreferences, TranslationKey> = {
  ALL_YEAR_SPORTS: 'search.aiSearch.preferences.allYearSports',
  ANY_DOCTOR: 'search.aiSearch.preferences.anyDoctor',
  ARTS_CENTRE: 'search.aiSearch.preferences.artsCentre',
  ATM: 'search.aiSearch.preferences.atm',
  BAKERY: 'search.aiSearch.preferences.bakery',
  BANK: 'search.aiSearch.preferences.bank',
  BAR_PUB: 'search.aiSearch.preferences.barPub',
  BEVERAGES: 'search.aiSearch.preferences.beverages',
  BEVERAGES_SHOP: 'search.aiSearch.preferences.beveragesShop',
  BIERGARTEN: 'search.aiSearch.preferences.biergarten',
  BOWLING: 'search.aiSearch.preferences.bowling',
  BREAD: 'search.aiSearch.preferences.bread',
  BUTCHER: 'search.aiSearch.preferences.butcher',
  CAFE: 'search.aiSearch.preferences.cafe',
  CHEMIST: 'search.aiSearch.preferences.chemist',
  CHILD_FRIENDLINESS: 'search.aiSearch.preferences.childFriendliness',
  CINEMA: 'search.aiSearch.preferences.cinema',
  CLASSICAL_CULTURE: 'search.aiSearch.preferences.classicalCulture',
  CLASSICAL_DANCE: 'search.aiSearch.preferences.classicalDance',
  CLOTHES: 'search.aiSearch.preferences.clothes',
  CLOTHES_AND_ACCESSORIES: 'search.aiSearch.preferences.clothesAndAccessories',
  COMPUTER_SHOPS: 'search.aiSearch.preferences.computerShops',
  CONCERT: 'search.aiSearch.preferences.concert',
  CONCERT_HALL: 'search.aiSearch.preferences.concertHall',
  CONVENIENCE_STORE: 'search.aiSearch.preferences.convenienceStore',
  CULTURE: 'search.aiSearch.preferences.culture',
  DAILY_NEEDS: 'search.aiSearch.preferences.dailyNeeds',
  DAIRY_PRODUCTS: 'search.aiSearch.preferences.dairyProducts',
  DANCING: 'search.aiSearch.preferences.dancing',
  DEPARTMENT_STORE: 'search.aiSearch.preferences.departmentStore',
  DO_IT_YOURSELF_SHOP: 'search.aiSearch.preferences.doItYourselfShop',
  DRINKING: 'search.aiSearch.preferences.drinking',
  EATING_OUT: 'search.aiSearch.preferences.eatingOut',
  EDUCATION: 'search.aiSearch.preferences.education',
  ELECTRONIC_DEVICES_SHOPS: 'search.aiSearch.preferences.electronicDevicesShops',
  ELECTRONICS_SHOPS: 'search.aiSearch.preferences.electronicsShops',
  EVERYDAY_ESSENTIALS: 'search.aiSearch.preferences.everydayEssentials',
  FARM_SHOP: 'search.aiSearch.preferences.farmShop',
  FAST_FOOD: 'search.aiSearch.preferences.fastFood',
  FLIGHT_NOISE_DAY: 'search.aiSearch.preferences.flightNoiseDay',
  FLIGHT_NOISE_NIGHT: 'search.aiSearch.preferences.flightNoiseNight',
  FOOD_SHOP: 'search.aiSearch.preferences.foodShop',
  FRUIT: 'search.aiSearch.preferences.fruit',
  FURNITURE_SHOP: 'search.aiSearch.preferences.furnitureShop',
  GALLERY: 'search.aiSearch.preferences.gallery',
  GENERAL_SHOP: 'search.aiSearch.preferences.generalShop',
  GREEN_GROCER: 'search.aiSearch.preferences.greenGrocer',
  HAIRDRESSER: 'search.aiSearch.preferences.hairdresser',
  HEALTH: 'search.aiSearch.preferences.health',
  HIFI_SHOPS: 'search.aiSearch.preferences.hifiShops',
  HIGHWAY: 'search.aiSearch.preferences.highway',
  HIKING: 'search.aiSearch.preferences.hiking',
  HOME_SHOP: 'search.aiSearch.preferences.homeShop',
  HOSPITAL: 'search.aiSearch.preferences.hospital',
  HOUSEWARE_SHOP: 'search.aiSearch.preferences.housewareShop',
  ICE_CREAM: 'search.aiSearch.preferences.iceCream',
  ICE_RINK: 'search.aiSearch.preferences.iceRink',
  INDOOR_FITNESS_SPORTS: 'search.aiSearch.preferences.indoorFitnessSports',
  INTERNATIONAL_AIRPORT: 'search.aiSearch.preferences.internationalAirport',
  JEWELRY: 'search.aiSearch.preferences.jewelry',
  KIOSK: 'search.aiSearch.preferences.kiosk',
  LIBRARY: 'search.aiSearch.preferences.library',
  LOCAL_GREEN_SPACES: 'search.aiSearch.preferences.localGreenSpaces',
  LOCAL_NATURE: 'search.aiSearch.preferences.localNature',
  LONG_DISTANCE_TRAVEL: 'search.aiSearch.preferences.longDistanceTravel',
  MAIL: 'search.aiSearch.preferences.mail',
  MAIN_MEAL: 'search.aiSearch.preferences.mainMeal',
  MEAT_SUPPLY: 'search.aiSearch.preferences.meatSupply',
  MOBILE_PHONES: 'search.aiSearch.preferences.mobilePhones',
  MONEY: 'search.aiSearch.preferences.money',
  MOUNTAIN_BIKING_TRAIL: 'search.aiSearch.preferences.mountainBikingTrail',
  MUSEUM: 'search.aiSearch.preferences.museum',
  MUSIC_EVENTS: 'search.aiSearch.preferences.musicEvents',
  MUSIC_SCHOOL: 'search.aiSearch.preferences.musicSchool',
  NATURE: 'search.aiSearch.preferences.nature',
  NATURE_HIGHLIGHTS: 'search.aiSearch.preferences.natureHighlights',
  NEIGHBORHOOD_FARMLAND: 'search.aiSearch.preferences.neighborhoodFarmland',
  NEIGHBORHOOD_FRUIT_FARM: 'search.aiSearch.preferences.neighborhoodFruitFarm',
  NEIGHBORHOOD_GREEN_SPACES: 'search.aiSearch.preferences.neighborhoodGreenSpaces',
  NEIGHBORHOOD_NATURE: 'search.aiSearch.preferences.neighborhoodNature',
  NIGHTCLUB: 'search.aiSearch.preferences.nightclub',
  NIGHTLIFE: 'search.aiSearch.preferences.nightlife',
  OUTDOOR_FITNESS: 'search.aiSearch.preferences.outdoorFitness',
  PHARMACY: 'search.aiSearch.preferences.pharmacy',
  PLANETARIUM: 'search.aiSearch.preferences.planetarium',
  POST_BOX: 'search.aiSearch.preferences.postBox',
  POST_OFFICE: 'search.aiSearch.preferences.postOffice',
  PRIMARY_OR_SECONDARY_SCHOOL: 'search.aiSearch.preferences.primaryOrSecondarySchool',
  PRIMARY_SCHOOL: 'search.aiSearch.preferences.primarySchool',
  PUB: 'search.aiSearch.preferences.pub',
  PUBLIC_TRANSPORT: 'search.aiSearch.preferences.publicTransport',
  PUBLIC_TRANSPORT_BUS_OVERALL: 'search.aiSearch.preferences.publicTransportBusOverall',
  PUBLIC_TRANSPORT_HIGHSPEED_TRAIN_OVERALL: 'search.aiSearch.preferences.publicTransportHighspeedTrainOverall',
  PUBLIC_TRANSPORT_TRAIN_OVERALL: 'search.aiSearch.preferences.publicTransportTrainOverall',
  REGIONAL_NATURE: 'search.aiSearch.preferences.regionalNature',
  RESTAURANT: 'search.aiSearch.preferences.restaurant',
  SAILING_AND_MOTORBOAT: 'search.aiSearch.preferences.sailingAndMotorboat',
  SCHOOL: 'search.aiSearch.preferences.school',
  SEA: 'search.aiSearch.preferences.sea',
  SECONDARY_SCHOOL: 'search.aiSearch.preferences.secondarySchool',
  SHOPPING: 'search.aiSearch.preferences.shopping',
  SHOPPING_MALLS: 'search.aiSearch.preferences.shoppingMalls',
  SNACKS: 'search.aiSearch.preferences.snacks',
  SPECIAL_CULTURE: 'search.aiSearch.preferences.specialCulture',
  SPECIALTY_FOODS: 'search.aiSearch.preferences.specialtyFoods',
  SPORTS: 'search.aiSearch.preferences.sports',
  SPORTS_BALL_SPORTS: 'search.aiSearch.preferences.sportsBallSports',
  SPORTS_BASKETBALL: 'search.aiSearch.preferences.sportsBasketball',
  SPORTS_CANOE: 'search.aiSearch.preferences.sportsCanoe',
  SPORTS_CLIMBING_INDOOR: 'search.aiSearch.preferences.sportsClimbingIndoor',
  SPORTS_HANDBALL: 'search.aiSearch.preferences.sportsHandball',
  SPORTS_INDOOR_SWIMMING: 'search.aiSearch.preferences.sportsIndoorSwimming',
  SPORTS_OUTDOOR_SWIMMING: 'search.aiSearch.preferences.sportsOutdoorSwimming',
  SPORTS_ROWING: 'search.aiSearch.preferences.sportsRowing',
  SPORTS_SOCCER: 'search.aiSearch.preferences.sportsSoccer',
  SPORTS_SWIMMING: 'search.aiSearch.preferences.sportsSwimming',
  SPORTS_TABLE_TENNIS: 'search.aiSearch.preferences.sportsTableTennis',
  SPORTS_TENNIS: 'search.aiSearch.preferences.sportsTennis',
  SUPERMARKET: 'search.aiSearch.preferences.supermarket',
  SWEETS_AND_SNACKS: 'search.aiSearch.preferences.sweetsAndSnacks',
  THEATRE: 'search.aiSearch.preferences.theatre',
  THEATRICAL_PERFORMANCE: 'search.aiSearch.preferences.theatricalPerformance',
  UNIVERSITY: 'search.aiSearch.preferences.university',
  VARIETY_STORE: 'search.aiSearch.preferences.varietyStore',
  VEGETABLES: 'search.aiSearch.preferences.vegetables',
  VICINITY_FARMLAND: 'search.aiSearch.preferences.vicinityFarmland',
  VICINITY_FRUIT_FARM: 'search.aiSearch.preferences.vicinityFruitFarm',
  VICINITY_GREEN_SPACES: 'search.aiSearch.preferences.vicinityGreenSpaces',
  VICINITY_NATURE: 'search.aiSearch.preferences.vicinityNature',
  WATER: 'search.aiSearch.preferences.water',
  WATER_SPORTS: 'search.aiSearch.preferences.waterSports',
  ZOO: 'search.aiSearch.preferences.zoo',
};
